import { useEffect, useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export function useBelotTrickAnimations({
  engineState,
  play,
  humanSeat,
  setTrickAnim,
  trickAnim,
  setLastTrickSnapshot,
  setPeekLastTrickOpen,
  trickOverlayCardRefs,
  trickCollectTlRef,
  trickRevealToCollectRef,
  prevTrickLenRef,
  lastFourTrickRef,
  fanPlayedRoundRef,
  handSlotRefs,
  getRelativeSeatPosition,
  trickWinFly,
  trickTakenBadgeRefs,
}) {
  const lastAnimatedCompletedTrickRef = useRef(0);

  useEffect(() => {
    const trick = engineState.currentTrick;
    const len = Array.isArray(trick) ? trick.length : 0;
    if (len === 4) {
      lastFourTrickRef.current = trick.map((p) => ({ seat: p.seat, card: p.card }));
    }
    const prevLen = prevTrickLenRef.current;
    let cleanup;
    const completedTrick = engineState.lastCompletedTrick;
    const completedPlays = Array.isArray(completedTrick?.plays) ? completedTrick.plays : null;
    const completedTrickIndex = Number(completedTrick?.trickIndex) || 0;
    const canAnimateFromCompletedTrick =
      (engineState.phase === 'play' || engineState.phase === 'dealing') &&
      len === 0 &&
      completedPlays &&
      completedPlays.length === 4 &&
      completedTrickIndex > 0 &&
      completedTrickIndex !== lastAnimatedCompletedTrickRef.current;
    const canAnimateFromLegacyFallback =
      prevLen === 4 &&
      len === 0 &&
      (engineState.phase === 'play' || engineState.phase === 'dealing') &&
      lastFourTrickRef.current &&
      lastFourTrickRef.current.length === 4 &&
      completedTrickIndex === 0;
    if (canAnimateFromCompletedTrick || canAnimateFromLegacyFallback) {
      const plays = canAnimateFromCompletedTrick ? completedPlays : lastFourTrickRef.current;
      const winnerSeat = Number.isInteger(completedTrick?.winnerSeat)
        ? completedTrick.winnerSeat
        : (engineState.lastTrickWinnerSeat ?? 0);
      const completedIdx = canAnimateFromCompletedTrick
        ? completedTrickIndex
        : (engineState.trickIndex > 8 ? 8 : Math.max(1, engineState.trickIndex - 1));
      setLastTrickSnapshot({
        plays: plays.map((p) => ({ ...p })),
        winnerSeat,
        completedIdx,
      });
      setTrickAnim({
        plays: plays.map((p) => ({ ...p })),
        winnerSeat,
        phase: 'reveal',
        token: Date.now(),
      });
      play('belot-trick-collect', 0.48);
      trickRevealToCollectRef.current?.kill();
      // Keep all 4 cards on table for 1s before starting collect animation.
      trickRevealToCollectRef.current = gsap.delayedCall(1.0, () => {
        trickRevealToCollectRef.current = null;
        setTrickAnim((a) => (a ? { ...a, phase: 'collect' } : null));
      });
      cleanup = () => {
        trickRevealToCollectRef.current?.kill();
        trickRevealToCollectRef.current = null;
      };
      lastAnimatedCompletedTrickRef.current = completedIdx;
    } else if (completedTrickIndex < lastAnimatedCompletedTrickRef.current) {
      // New round/match can decrease trick index; allow animation again from trick 1.
      lastAnimatedCompletedTrickRef.current = 0;
    }
    prevTrickLenRef.current = len;
    return cleanup;
  }, [
    engineState.currentTrick,
    engineState.lastCompletedTrick,
    engineState.phase,
    engineState.trickIndex,
    engineState.lastTrickWinnerSeat,
    play,
    lastFourTrickRef,
    prevTrickLenRef,
    setLastTrickSnapshot,
    setTrickAnim,
    trickRevealToCollectRef,
  ]);

  useLayoutEffect(() => {
    if (!trickAnim || trickAnim.phase !== 'collect' || !Array.isArray(trickAnim.plays) || trickAnim.plays.length !== 4) {
      return undefined;
    }
    trickCollectTlRef.current?.kill();
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    const cards = trickOverlayCardRefs.current.filter(Boolean);
    const finish = () => {
      trickCollectTlRef.current = null;
      if (cards.length) gsap.set(cards, { clearProps: 'transform,opacity,boxShadow' });
      setTrickAnim(null);
    };
    if (reduced || cards.length !== 4) {
      finish();
      return undefined;
    }
    const winnerPos =
      trickAnim.winnerSeat != null ? getRelativeSeatPosition(trickAnim.winnerSeat, humanSeat) : 'bottom';
    const fly = trickWinFly[winnerPos] || trickWinFly.bottom;
    const winnerBadgeEl =
      trickAnim.winnerSeat != null && trickTakenBadgeRefs?.current
        ? trickTakenBadgeRefs.current[trickAnim.winnerSeat]
        : null;
    let badgeVector = null;
    if (winnerBadgeEl && cards.length) {
      const badgeRect = winnerBadgeEl.getBoundingClientRect?.();
      const cardRect = cards[0].getBoundingClientRect?.();
      if (badgeRect && cardRect && badgeRect.width > 0 && badgeRect.height > 0 && cardRect.width > 0 && cardRect.height > 0) {
        const cardCenterX = cardRect.left + cardRect.width / 2;
        const cardCenterY = cardRect.top + cardRect.height / 2;
        const badgeCenterX = badgeRect.left + badgeRect.width / 2;
        const badgeCenterY = badgeRect.top + badgeRect.height / 2;
        badgeVector = { x: badgeCenterX - cardCenterX, y: badgeCenterY - cardCenterY };
      }
    }
    const stackGapPx = 2.2;
    const t0 = Number(engineState.tricksWonByTeam?.[0]) || 0;
    const t1 = Number(engineState.tricksWonByTeam?.[1]) || 0;
    const isKapotCollectGlow = Math.max(t0, t1) === 8;
    gsap.set(cards, { opacity: 1, transformOrigin: '50% 50%', force3D: true });
    const tl = gsap.timeline({ onComplete: finish });
    trickCollectTlRef.current = tl;
    // Phase 1: gather into a tight vertical stack (no "diamond" offsets).
    tl.to(cards, {
      duration: 0.18,
      ease: 'sine.out',
      stagger: 0,
      x: 0,
      y: (_, i) => i * stackGapPx,
      scale: 1,
      rotation: 0,
      skewX: 0,
      opacity: 1,
      boxShadow: isKapotCollectGlow
        ? '0 0 26px rgba(251, 191, 36, 0.72), 0 0 10px rgba(250, 204, 21, 0.45)'
        : '0 0 0 rgba(0,0,0,0)',
    });
    // Phase 2: fly to the 🃏 badge while smoothly shrinking to 0 (100% -> 0%).
    tl.to(cards, {
      duration: 0.34,
      ease: 'power2.inOut',
      stagger: 0,
      x: () => (badgeVector ? badgeVector.x : fly.x),
      y: (_, i) => {
        const stackDy = i * stackGapPx;
        const targetX = badgeVector ? badgeVector.x : fly.x;
        const targetY = (badgeVector ? badgeVector.y : fly.y) + stackDy;
        const dist = Math.hypot(targetX, targetY);
        const arcLift = clamp(dist * 0.07, 6, 14);
        return targetY - arcLift;
      },
      scale: 0,
      rotation: 0,
      opacity: 0,
    });
    return () => {
      tl.kill();
      trickCollectTlRef.current = null;
      gsap.set(cards, { clearProps: 'transform,opacity,boxShadow' });
    };
  }, [
    trickAnim,
    trickCollectTlRef,
    trickOverlayCardRefs,
    setTrickAnim,
    getRelativeSeatPosition,
    humanSeat,
    trickWinFly,
    trickTakenBadgeRefs,
    engineState.tricksWonByTeam,
  ]);

  useEffect(() => {
    const ph = engineState.phase;
    if (ph === 'auction' || ph === 'declare') {
      trickRevealToCollectRef.current?.kill();
      trickRevealToCollectRef.current = null;
      trickCollectTlRef.current?.kill();
      trickCollectTlRef.current = null;
      setTrickAnim(null);
      // Оставляем lastTrickSnapshot: полоса «Последняя взятка» над сукном (и в bazar/declare).
      lastAnimatedCompletedTrickRef.current = 0;
      return;
    }
    if (ph === 'init' || ph === 'match_end') {
      trickRevealToCollectRef.current?.kill();
      trickRevealToCollectRef.current = null;
      trickCollectTlRef.current?.kill();
      trickCollectTlRef.current = null;
      setTrickAnim(null);
      setLastTrickSnapshot(null);
      lastAnimatedCompletedTrickRef.current = 0;
      return;
    }
    if (ph === 'play' && (engineState.currentTrick?.length ?? 0) > 0) {
      setLastTrickSnapshot(null);
    }
  }, [
    engineState.phase,
    engineState.currentTrick,
    engineState.trickIndex,
    setLastTrickSnapshot,
    setTrickAnim,
    trickRevealToCollectRef,
    trickCollectTlRef,
  ]);

  useEffect(() => {
    if (engineState.phase !== 'play') setPeekLastTrickOpen(false);
  }, [engineState.phase, setPeekLastTrickOpen]);

  useEffect(() => {
    if (engineState.phase !== 'play' || engineState.trickIndex !== 1) return undefined;
    const ri = engineState.roundIndex;
    if (fanPlayedRoundRef.current === ri) return undefined;
    fanPlayedRoundRef.current = ri;
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
    ) {
      return undefined;
    }
    const targets = handSlotRefs.map((r) => r.current).filter(Boolean);
    if (!targets.length) return undefined;
    gsap.fromTo(
      targets,
      { scale: 0.86, y: 20, opacity: 0.72 },
      {
        scale: 1,
        y: 0,
        opacity: 1,
        duration: 0.38,
        ease: 'power2.out',
        stagger: 0.038,
      },
    );
    return undefined;
  }, [engineState.phase, engineState.trickIndex, engineState.roundIndex, handSlotRefs, fanPlayedRoundRef]);
}

